import { useEffect, useRef, useState } from 'react';
import { Pencil, X, Check } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/cn';

interface InlineEditProps {
  value: string | undefined | null;
  onSave: (value: string) => Promise<void> | void;
  placeholder?: string;
  display?: (value: string) => React.ReactNode;
  className?: string;
  emptyLabel?: string;
  type?: 'text' | 'url' | 'email' | 'tel';
}

export function InlineEdit({
  value,
  onSave,
  placeholder,
  display,
  className,
  emptyLabel = '— clique pra adicionar',
  type = 'text',
}: InlineEditProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? '');
  const [saving, setSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!editing) setDraft(value ?? '');
  }, [value, editing]);

  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  async function commit() {
    const trimmed = draft.trim();
    if (trimmed === (value ?? '')) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      await onSave(trimmed);
      setEditing(false);
    } finally {
      setSaving(false);
    }
  }

  function cancel() {
    setDraft(value ?? '');
    setEditing(false);
  }

  if (editing) {
    return (
      <div className="inline-flex items-center gap-1">
        <Input
          ref={inputRef}
          type={type}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commit();
            else if (e.key === 'Escape') cancel();
          }}
          onBlur={() => {
            setTimeout(() => {
              if (document.activeElement?.getAttribute('data-inline-action') !== 'true') {
                commit();
              }
            }, 100);
          }}
          placeholder={placeholder}
          className="h-8"
          disabled={saving}
        />
        <button
          type="button"
          data-inline-action="true"
          onMouseDown={(e) => e.preventDefault()}
          onClick={commit}
          disabled={saving}
          className="inline-flex h-7 w-7 items-center justify-center rounded text-green-600 hover:bg-green-100"
          aria-label="Salvar"
        >
          <Check className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          data-inline-action="true"
          onMouseDown={(e) => e.preventDefault()}
          onClick={cancel}
          disabled={saving}
          className="inline-flex h-7 w-7 items-center justify-center rounded text-muted-foreground hover:bg-muted"
          aria-label="Cancelar"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => setEditing(true)}
      className={cn(
        'group inline-flex items-center gap-1 text-left rounded hover:bg-muted/50 px-1 -mx-1',
        !value && 'text-muted-foreground italic',
        className,
      )}
    >
      {value ? display ? display(value) : <span>{value}</span> : <span>{emptyLabel}</span>}
      <Pencil className="h-3 w-3 opacity-0 group-hover:opacity-100 text-muted-foreground" />
    </button>
  );
}

interface InlineTextareaProps {
  value: string | undefined | null;
  onSave: (value: string) => Promise<void> | void;
  placeholder?: string;
  emptyLabel?: string;
  rows?: number;
}

export function InlineTextarea({
  value,
  onSave,
  placeholder,
  emptyLabel = '— clique pra adicionar notas',
  rows = 4,
}: InlineTextareaProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? '');
  const [saving, setSaving] = useState(false);
  const ref = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (!editing) setDraft(value ?? '');
  }, [value, editing]);

  useEffect(() => {
    if (editing) ref.current?.focus();
  }, [editing]);

  async function commit() {
    const trimmed = draft.trim();
    if (trimmed === (value ?? '')) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      await onSave(trimmed);
      setEditing(false);
    } finally {
      setSaving(false);
    }
  }

  if (editing) {
    return (
      <div className="space-y-1">
        <textarea
          ref={ref}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              setDraft(value ?? '');
              setEditing(false);
            }
          }}
          placeholder={placeholder}
          rows={rows}
          disabled={saving}
          className="w-full rounded-md border bg-background px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
        />
        <div className="flex gap-1">
          <button
            type="button"
            onClick={commit}
            disabled={saving}
            className="inline-flex items-center gap-1 rounded bg-primary px-2 py-1 text-xs text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            <Check className="h-3 w-3" />
            Salvar
          </button>
          <button
            type="button"
            onClick={() => {
              setDraft(value ?? '');
              setEditing(false);
            }}
            disabled={saving}
            className="inline-flex items-center gap-1 rounded border px-2 py-1 text-xs hover:bg-muted"
          >
            <X className="h-3 w-3" />
            Cancelar
          </button>
        </div>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => setEditing(true)}
      className="group block w-full text-left rounded-md hover:bg-muted/50 px-2 py-1.5 -mx-2"
    >
      {value ? (
        <div className="flex items-start justify-between gap-2">
          <p className="text-sm whitespace-pre-wrap">{value}</p>
          <Pencil className="h-3 w-3 mt-1 opacity-0 group-hover:opacity-100 text-muted-foreground shrink-0" />
        </div>
      ) : (
        <span className="text-sm italic text-muted-foreground">{emptyLabel}</span>
      )}
    </button>
  );
}

interface InlineTagsProps {
  value: string[] | undefined;
  onSave: (tags: string[]) => Promise<void> | void;
}

export function InlineTags({ value, onSave }: InlineTagsProps) {
  const [editing, setEditing] = useState(false);
  const [tags, setTags] = useState<string[]>(value ?? []);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const ref = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!editing) setTags(value ?? []);
  }, [value, editing]);

  useEffect(() => {
    if (editing) ref.current?.focus();
  }, [editing]);

  function addTag() {
    const t = draft.trim().replace(/^#/, '');
    if (t && !tags.includes(t)) {
      setTags([...tags, t]);
    }
    setDraft('');
  }

  function removeTag(t: string) {
    setTags(tags.filter((x) => x !== t));
  }

  async function commit() {
    setSaving(true);
    try {
      await onSave(tags);
      setEditing(false);
    } finally {
      setSaving(false);
    }
  }

  if (editing) {
    return (
      <div className="space-y-2">
        <div className="flex flex-wrap items-center gap-1.5">
          {tags.map((t) => (
            <span
              key={t}
              className="inline-flex items-center gap-1 rounded-md border bg-card px-2 py-0.5 text-xs"
            >
              #{t}
              <button
                type="button"
                onClick={() => removeTag(t)}
                className="text-muted-foreground hover:text-destructive"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
          <input
            ref={ref}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ',') {
                e.preventDefault();
                addTag();
              } else if (e.key === 'Backspace' && !draft && tags.length > 0) {
                setTags(tags.slice(0, -1));
              }
            }}
            placeholder="nova tag..."
            className="h-6 min-w-24 flex-1 border-none bg-transparent px-1 text-xs outline-none"
          />
        </div>
        <div className="flex gap-1">
          <button
            type="button"
            onClick={commit}
            disabled={saving}
            className="inline-flex items-center gap-1 rounded bg-primary px-2 py-1 text-xs text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            <Check className="h-3 w-3" /> Salvar
          </button>
          <button
            type="button"
            onClick={() => {
              setTags(value ?? []);
              setEditing(false);
            }}
            className="inline-flex items-center gap-1 rounded border px-2 py-1 text-xs hover:bg-muted"
          >
            <X className="h-3 w-3" /> Cancelar
          </button>
        </div>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => setEditing(true)}
      className="group flex flex-wrap items-center gap-1.5 rounded hover:bg-muted/50 px-1 -mx-1"
    >
      {value && value.length > 0 ? (
        value.map((t) => (
          <span key={t} className="rounded border bg-card px-2 py-0.5 text-xs">
            #{t}
          </span>
        ))
      ) : (
        <span className="text-xs italic text-muted-foreground">— sem tags</span>
      )}
      <Pencil className="h-3 w-3 opacity-0 group-hover:opacity-100 text-muted-foreground" />
    </button>
  );
}

interface InlineRedesProps {
  value: Record<string, string> | undefined;
  onSave: (redes: Record<string, string>) => Promise<void> | void;
}

const REDES_KEYS = ['instagram', 'linkedin', 'youtube', 'facebook', 'twitter', 'tiktok'];

export function InlineRedes({ value, onSave }: InlineRedesProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!editing) setDraft({ ...(value ?? {}) });
  }, [value, editing]);

  async function commit() {
    setSaving(true);
    try {
      const cleaned: Record<string, string> = {};
      for (const [k, v] of Object.entries(draft)) {
        const t = (v ?? '').trim();
        if (t) cleaned[k] = t;
      }
      await onSave(cleaned);
      setEditing(false);
    } finally {
      setSaving(false);
    }
  }

  if (editing) {
    return (
      <div className="space-y-2 rounded-md border bg-card p-3">
        <p className="text-xs font-medium text-muted-foreground">Redes sociais</p>
        <div className="grid gap-2 sm:grid-cols-2">
          {REDES_KEYS.map((k) => (
            <div key={k} className="flex items-center gap-2">
              <label className="w-20 text-xs capitalize text-muted-foreground">{k}</label>
              <Input
                value={draft[k] ?? ''}
                onChange={(e) => setDraft({ ...draft, [k]: e.target.value })}
                placeholder={`URL do ${k}`}
                className="h-7 text-xs"
              />
            </div>
          ))}
        </div>
        <div className="flex gap-1 pt-1">
          <button
            type="button"
            onClick={commit}
            disabled={saving}
            className="inline-flex items-center gap-1 rounded bg-primary px-2 py-1 text-xs text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            <Check className="h-3 w-3" /> Salvar
          </button>
          <button
            type="button"
            onClick={() => {
              setDraft({ ...(value ?? {}) });
              setEditing(false);
            }}
            className="inline-flex items-center gap-1 rounded border px-2 py-1 text-xs hover:bg-muted"
          >
            <X className="h-3 w-3" /> Cancelar
          </button>
        </div>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => setEditing(true)}
      className="group inline-flex items-center gap-1 rounded hover:bg-muted/50 px-1 -mx-1 text-xs text-muted-foreground"
    >
      <Pencil className="h-3 w-3" />
      Editar redes
    </button>
  );
}
